import React, { Component } from 'react';
import axios from 'axios';
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { sortBy } from 'lodash';
// material
import { Table, TableBody, TableCell, TableHead, TableRow, Paper, Container, Link, Grid, IconButton } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
// app
import { updateSession, setApiUrls, addAuth, openSnackbar, closeSnackbar } from '../../../store/action-creators';
// containers
import AdminContainer from '../../../containers/Admin';
// models
import { Auth, Session } from '../../../entities/auth';
import { APIUrl, Campaign, campaignStatusListRun, CampaignStats } from '../../../entities/site';
// components
import Loading from '../../../components/site/Loading';
import ActionButton from '../../../components/buttons/ActionButton';
import PieChart from './components/PieChart';
import SelectInput, { SelectItem } from '../../../components/inputs/SelectInput';
import EditCampaign from './components/EditCampaign';
// services
import AuthService from '../../../services/auth';

const useProd = true;

const styles: any = {
  tableWrapper: {
    maxHeight: 440,
    overflow: 'auto',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    paddingTop: 10,
    paddingBottom: 10,
    textAlign: 'left',
    color: '#1C242D'
  },
  date: {
    fontSize: 20,
    fontWeight: 'bold',
    paddingTop: 10,
    paddingBottom: 10,
    textAlign: 'right',
    color: '#627995'
  },
  gridLink: {
    cursor: 'pointer',
    fontSize: 16,
    color: '#00ABC7'
  },
  gridSent: {
    color: '#627995',
    fontSize: 16,
    fontWeight: 'bold',
    paddingLeft: 10,
    paddingTop: 5
  },
  gridStat: {
    color: '#627995',
    ontSize: 16
  },
  gridStatus: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#29E2BF'
  },
  message: {
    padding: 20,
    fontSize: 14,
    textAlign: 'center'
  }
}

interface AdminCampaignsRunPageProps {
  history: any;
  auth: Auth;
  session: Session;
  apiUrls: APIUrl;
  updateSession(session: Session): void;
  setApiUrls(urls: APIUrl): void;
  openSnackbar(message: string): void;
  closeSnackbar(): void;
}

interface AdminCampaignsRunPageState {
  loading: boolean;
  openEditCampaign: boolean;
  campaigns: Campaign[];
  lastKey: any;
  selectedCampaignId: string;
  status: string;
  statuses: SelectItem[];
  filter: string;
  filters: SelectItem[];
}

class AdminCampaignsRunPage extends Component<AdminCampaignsRunPageProps, AdminCampaignsRunPageState> {

  constructor(props: AdminCampaignsRunPageProps) {
    super(props);
    this.state = {
      loading: false,
      openEditCampaign: false,
      campaigns: [],
      lastKey: null,
      selectedCampaignId: '',
      status: 'new',
      statuses: campaignStatusListRun,
      filter: 'all',
      filters: [
        {
          name: 'All',
          value: 'all'
        },
        {
          name: 'Click Throughs',
          value: 'totalClicks'
        },
        {
          name: 'Sent',
          value: 'totalSent'
        },
        {
          name: 'Do Not Call',
          value: 'totalDoNotCall'
        },
        {
          name: 'Non Responsive',
          value: 'totalNonResponsive'
        },
        {
          name: 'Opt Outs',
          value: 'totalOptOuts'
        },
        {
          name: 'Contacts',
          value: 'totalContacts'
        }
      ]
    }
  }

  componentDidMount = async () => {
    this.fetchCampaigns(this.state.status, null);
  }

  fetchCampaigns = async (status: string, lastKey: any) => {
    try {
      this.setState({
        loading: true
      });
      
      let url = `${AuthService.getApiUrls().site}/campaigns?status=${status}`;
      if (lastKey) {
        url += `&keyId=${lastKey.keyId}&keyStatus=${lastKey.keyStatus ?? ''}&keyCreatedDate=${lastKey.keyCreatedDate ?? ''}`;
      }

      const campaignsRes = await axios.get(url);

      let campaigns = this.state.campaigns;
      campaigns = campaigns.concat(campaignsRes.data.results);
      const filtered = sortBy(campaigns, 'createdDate').reverse();

      this.setState({
        loading: false,
        campaigns: filtered,
        lastKey: campaignsRes.data.lastKey,
      });
    } catch (error) {
      this.setState({ loading: true });
      console.log('error', error);
    }
  }

  handleLoadMore = async () => {
    try {
      await this.fetchCampaigns(this.state.status, this.state.lastKey);
    } catch (error) {
      console.log(error)
    }
  }

  handleRoute = (campaign: Campaign) => {
    this.props.history.push(`/admin/campaign/${campaign.id}/run`);
  }


  handleStatusChange = (value: string) => {
    if (value !== this.state.status) {
      this.setState({ 
        status: value,
        campaigns: []
      });
    } else {
      this.setState({ status: value });
    }
    
    this.fetchCampaigns(value, this.state.lastKey);
  }

  handleFilterChange = (value: string) => {
    this.setState({ filter: value });

    let filteredCampaigns = [...this.state.campaigns];

    if (value === 'all') {
      filteredCampaigns = sortBy(filteredCampaigns, 'name');
    } else {
      filteredCampaigns = sortBy(filteredCampaigns, value).reverse();
    }

    this.setState({ campaigns: filteredCampaigns });

  }

  handleUpdateStats = async (campaign: Campaign) => {
    try {

      this.setState({ loading: true });

      const statsRes = await axios.get(`${AuthService.getApiUrls().site}/campaign/${campaign.id}/stats`);
      const stats: CampaignStats = statsRes.data;

      campaign.totalContacts = stats.total;
      campaign.totalClicks = stats.totalClickThroughs;
      campaign.totalActive = stats.totalActive;
      campaign.totalDoNotCall = stats.totalDoNotCalls;
      campaign.totalNonResponsive = stats.totalNonResponsive;
      campaign.totalOptOuts = stats.totalOptOuts;
      campaign.totalApplications = stats.totalApplications;
      campaign.totalSent = stats.totalSent + stats.totalClickThroughs + stats.totalActive + stats.totalDoNotCalls + stats.totalNonResponsive + stats.totalOptOuts + stats.totalErrors + stats.totalApplications;

      await axios.put(`${AuthService.getApiUrls().site}/campaign/${campaign.id}`, campaign);

      const newCampaigns = this.state.campaigns.map(c => {
        if (c.id === campaign.id) {
          c.totalContacts = stats.total;
          c.totalClicks = stats.totalClickThroughs;
          c.totalActive = stats.totalActive;
          c.totalDoNotCall = stats.totalDoNotCalls;
          c.totalNonResponsive = stats.totalNonResponsive;
          c.totalOptOuts = stats.totalOptOuts;
          c.totalSent = stats.totalSent + stats.totalClickThroughs + stats.totalActive + stats.totalDoNotCalls + stats.totalNonResponsive + stats.totalOptOuts + stats.totalErrors;
        }
        return c;
      });

      this.setState({
        loading: false,
        campaigns: newCampaigns
      });

    } catch (error) {
      console.log(error);
    }
  }

  render() {

    return (
      <AdminContainer showOption={false} private={false} maxWidth={false}>
        <Loading open={this.state.loading} />
        <Container maxWidth="lg">
          <Grid container justify="space-between" style={{ marginBottom: 20 }} spacing={2}>
            <Grid item xs={12} sm={6}>
              <SelectInput
                title="Status"
                value={this.state.status}
                values={this.state.statuses}
                fullWidth
                onChange={(event: any) => this.handleStatusChange(event.target.value)}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={10}>
              <div style={styles.title}>Campaigns for today</div>
            </Grid>
            <Grid item xs={2}>
              <div style={styles.date}>{moment().format('MMM DD, YYYY')}</div>
            </Grid>
          </Grid>
          <Paper>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Contacts</TableCell>
                  <TableCell>Sent</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {this.state.campaigns.map((campaign, index) => (
                  <TableRow key={`campaign-${index}`}>
                    <TableCell component="th" scope="row"><Link style={styles.gridLink} onClick={() => this.handleRoute(campaign)}>{campaign.name}</Link></TableCell>
                    <TableCell>
                      <div style={{ display: 'flex' }}>
                        <div style={{ width: 30, height: 30 }}>
                          <PieChart size="sm" stat={campaign.totalSent} total={campaign.totalContacts} />
                        </div>
                        <div style={styles.gridSent}>{campaign.totalContacts}</div>
                      </div>
                    </TableCell>
                    <TableCell style={styles.gridStat}>{campaign.totalSent}</TableCell>
                    <TableCell style={styles.gridStatus}>{campaign.status}</TableCell>
                    <TableCell style={styles.gridStat}>{new Date(campaign.createdDate).toLocaleDateString()}</TableCell>
                    <TableCell style={styles.gridButton}>
                      <IconButton aria-label="sync campaign" onClick={() => this.handleUpdateStats(campaign)} >
                        <RefreshIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {this.state.lastKey !== null &&
              <ActionButton name="LOAD MORE" onClick={this.handleLoadMore} fullWidth />
            }
            {!this.state.campaigns.length &&
              <div style={styles.message}>No campaigns to display</div>
            }
          </Paper>
        </Container>
      </AdminContainer>
    )
  }


}

const mapStateToProps = (state: any) => {
  return {
    auth: state.auth.auth,
    session: state.session.session,
    apiUrls: state.site.apiUrls
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return bindActionCreators({ updateSession, setApiUrls, addAuth, openSnackbar, closeSnackbar }, dispatch);
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter<any, any>(AdminCampaignsRunPage));



// WEBPACK FOOTER //
// ./src/pages/admin/campaign/AdminCampaignsRun.tsx